import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from 'framer-motion';
import { AppShell } from './components/AppShell';
import { SignIn } from './pages/SignIn';
import { Home } from './pages/Home';
import { MyFiles } from './pages/MyFiles';
import { LoanDetail } from './pages/LoanDetail';
import { ReferBuyer } from './pages/ReferBuyer';
import { MyTeam } from './pages/MyTeam';
import { useChat } from './hooks/useChat';
import { brands, loans } from './data/portal';
import type { Loan, Page } from './types/portal';
import { preloadHeroImage } from './utils/heroImage';

// Start fetching + decoding the sign-in photo as soon as the app loads.
preloadHeroImage();
const ease = [0.23, 1, 0.32, 1] as const;
export function App() {
  const [signedIn, setSignedIn] = useState(false);
  const [page, setPage] = useState<Page>('home');
  const [loanId, setLoanId] = useState<string>(loans[0].id);
  const [escalationLoanId, setEscalationLoanId] = useState<string | null>(null);
  const chat = useChat();
  const reduceMotion = useReducedMotion();
  // Sign-in transition:
  //  'expanding' = hero panel widens to full screen;
  //  'covering'  = the portal mounts and paints UNDER the opaque full-screen hero (nothing visible changes);
  //  'revealing' = the hero overlay fades out (opacity only) to uncover the already-painted portal;
  //  then the overlay unmounts. The portal itself never animates in, so there is no blank or half-mounted frame.
  const [leaving, setLeaving] = useState<'no' | 'expanding' | 'covering' | 'revealing'>('no');
  const leavingRef = useRef(false);
  const signIn = () => {
    if (leavingRef.current || signedIn) return;
    if (reduceMotion) {
      setSignedIn(true);
      return;
    }
    leavingRef.current = true;
    window.scrollTo({
      top: 0
    });
    setLeaving('expanding');
  };
  const onHeroExpanded = () => {
    setSignedIn(true);
    setLeaving('covering');
  };
  const onRevealed = () => {
    if (!leavingRef.current) return;
    leavingRef.current = false;
    setLeaving('no');
  };

  // Wait until the freshly mounted portal has actually been painted (two frames) before starting the fade,
  // so the mount cost never eats into the reveal animation.
  useEffect(() => {
    if (leaving !== 'covering') return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setLeaving('revealing'));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [leaving]);

  // Safety net in case the overlay fade's animationend is skipped (e.g. background tab).
  useEffect(() => {
    if (leaving !== 'revealing') return;
    const t = window.setTimeout(onRevealed, 800);
    return () => window.clearTimeout(t);
  }, [leaving]);
  const brand = brands.nmhl;
  const brandVars = {
    '--brand': brand.primary,
    '--brand-dark': brand.primaryDark,
    '--brand-soft': brand.soft,
    '--ink': brand.ink
  } as React.CSSProperties;
  const navigate = (p: Page) => {
    setEscalationLoanId(null);
    setPage(p);
    window.scrollTo({
      top: 0
    });
  };
  const openLoan = (id: string) => {
    setLoanId(id);
    navigate('loan');
  };
  const askAbout = (loan: Loan) => {
    chat.setDraft(`What’s the latest on the ${loan.lastName} file?`);
    navigate('home');
  };
  const escalate = (loan: Loan) => {
    navigate('team');
    setEscalationLoanId(loan.id);
  };
  const currentLoan = loans.find(l => l.id === loanId) ?? loans[0];
  const content = {
    home: <Home brand={brand} messages={chat.messages} typing={chat.typing} draft={chat.draft} onDraftChange={chat.setDraft} onSend={chat.send} onOpenLoan={openLoan} onNavigate={navigate} data-id="element-0" data-mp="1w2ao61hf5a15" />,
    files: <MyFiles onOpenLoan={openLoan} data-id="element-1" data-mp="6onpydl54up2" />,
    loan: <LoanDetail loan={currentLoan} onBack={() => navigate('files')} onAsk={askAbout} onEscalate={escalate} data-id="element-2" data-mp="6mujayaj2ah2" />,
    refer: <ReferBuyer data-id="element-3" data-mp="anwk3jk3eul5" />,
    team: <MyTeam key={escalationLoanId ?? 'none'} escalationLoanId={escalationLoanId} data-id="element-4" data-mp="qx5h40meusbj" />
  }[page];
  return <MotionConfig reducedMotion="user" data-id="element-5" data-mp="m6p3ogilb6p6">
      <div style={brandVars} data-brand={brand.id} className="min-h-screen w-full bg-slate-50 font-sans text-slate-900" data-id="element-6" data-mp="m5ng5cke0rqk">
        {(!signedIn || leaving !== 'no') &&
      // During the transition the sign-in screen is pinned to the viewport ABOVE the portal. The portal mounts
      // underneath in normal flow at full opacity; the expanded hero then fades out on top of it.
      <div className={leaving === 'no' ? undefined : `fixed inset-0 overflow-hidden${leaving === 'revealing' ? ' animate-hero-out' : ''}`} style={leaving === 'no' ? undefined : {
        zIndex: 60
      }} onAnimationEnd={e => {
        if (leaving === 'revealing' && e.target === e.currentTarget) onRevealed();
      }} data-id="element-7" data-mp="14xyfiwfu27jx">
            <SignIn brand={brand} onSignIn={signIn} expanding={leaving !== 'no'} onExpanded={onHeroExpanded} data-id="element-8" data-mp="3gok7due90tsu" />
          </div>}
        {signedIn && <div
      // Rendered at full opacity with its final background from the first frame; revealed by the hero fading out.
      className="relative isolate min-h-screen bg-slate-50" data-id="element-9" data-mp="2071ymydd3ep0">
            <AppShell brand={brand} page={page} onNavigate={navigate} onSignOut={() => {
          setSignedIn(false);
          setPage('home');
        }} data-id="element-10" data-mp="29wlia68wfckx">
              <AnimatePresence mode="wait" initial={false} data-id="element-11" data-mp="2nvl6jcsi1pgk">
                <motion.div key={page === 'loan' ? `loan-${loanId}` : page} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4,
              transition: {
                duration: 0.12,
                ease: 'easeIn'
              }
            }} transition={{
              duration: 0.24,
              ease
            }} data-id="element-12" data-mp="2nw4cdv4vkne8">
                  {content}
                </motion.div>
              </AnimatePresence>
            </AppShell>
          </div>}
      </div>
    </MotionConfig>;
}